import React from 'react'
import { CompanyIntelligenceResponse } from '../services/jobApplicationService'
import { COMPANY_INTEL_CATEGORY_LABELS, CompanyIntelligenceCategoryKey } from './companyIntelligenceCategories'
import './CompanyIntelligenceDisplay.css'

interface CompanyIntelligenceDisplayProps {
  intelligence: CompanyIntelligenceResponse | null
  isLoading?: boolean
  error?: string | null
  hideOverallScore?: boolean
  onCategoryDeepDive?: (category: CompanyIntelligenceCategoryKey) => void
  deepDiveActiveCategory?: CompanyIntelligenceCategoryKey | null
  deepDiveLoading?: boolean
  deepDiveDisabled?: boolean
  deepDiveDisabledMessage?: string
}

export const CompanyIntelligenceDisplay: React.FC<CompanyIntelligenceDisplayProps> = ({
  intelligence,
  isLoading = false,
  error = null,
  hideOverallScore = false,
  onCategoryDeepDive,
  deepDiveActiveCategory = null,
  deepDiveLoading = false,
  deepDiveDisabled = false,
  deepDiveDisabledMessage = 'Expanded search is not available in your current plan.'
}) => {
  if (isLoading) {
    return (
      <div className="company-intelligence">
        <div className="intelligence-header">
          <h3>🏢 Company Intelligence</h3>
        </div>
        <div className="intelligence-loading">
          <div className="spinner"></div>
          <p>Gathering company intelligence... This usually takes about 30-60 seconds.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="company-intelligence">
        <div className="intelligence-header">
          <h3>🏢 Company Intelligence</h3>
        </div>
        <div className="intelligence-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    )
  }

  if (!intelligence) {
    return null
  }

  // Handle processing status
  if (intelligence.status === 'processing') {
    return (
      <div className="company-intelligence">
        <div className="intelligence-header">
          <h3>🏢 Company Intelligence</h3>
        </div>
        <div className="intelligence-loading">
          <div className="spinner"></div>
          <p>Company intelligence is being processed. Please check back in about 30-60 seconds.</p>
        </div>
      </div>
    )
  }

  // Handle clarification needed
  if (intelligence.status === 'need_clarification') {
    return (
      <div className="company-intelligence">
        <div className="intelligence-header">
          <h3>🏢 Company Intelligence</h3>
        </div>
        <div className="intelligence-clarification">
          <p className="clarification-message">{intelligence.message}</p>
          {intelligence.candidates && intelligence.candidates.length > 0 && (
            <div className="candidates-list">
              <p className="candidates-title">Possible matches:</p>
              <ul>
                {intelligence.candidates.map((candidate) => (
                  <li key={candidate.id}>
                    <strong>{candidate.name}</strong> - {candidate.hq} ({(candidate.confidence * 100).toFixed(0)}% match)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Handle no data
  if (intelligence.status === 'no_data') {
    return (
      <div className="company-intelligence">
        <div className="intelligence-header">
          <h3>🏢 Company Intelligence</h3>
        </div>
        <div className="intelligence-no-data">
          <p>ℹ️ {intelligence.message || 'No public information found for this company.'}</p>
        </div>
      </div>
    )
  }

  // Show intelligence data
  if (intelligence.status !== 'ok' || !intelligence.categories) {
    return null
  }

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'excellent'
    if (score >= 50) return 'good'
    if (score >= 30) return 'moderate'
    return 'low'
  }

  const getScoreLabel = (score: number): string => {
    if (score >= 70) return 'Positive'
    if (score >= 50) return 'Neutral'
    if (score >= 30) return 'Concerning'
    return 'Negative'
  }

  const getOverallScoreColor = (score: number): string => {
    if (score >= 70) return 'excellent'
    if (score >= 50) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const renderDeepDiveButton = (categoryKey: CompanyIntelligenceCategoryKey) => {
    if (!onCategoryDeepDive) {
      return null
    }

    const isButtonLoading = deepDiveLoading && deepDiveActiveCategory === categoryKey
    const buttonLabel = isButtonLoading ? 'Loading…' : 'Deep Dive'
    const disabled = deepDiveDisabled || isButtonLoading
    const title = deepDiveDisabled ? deepDiveDisabledMessage : 'Run expanded search (costs tokens)'

    return (
      <button
        type="button"
        className={`category-deep-dive-button ${isButtonLoading ? 'is-loading' : ''}`}
        onClick={() => onCategoryDeepDive(categoryKey)}
        disabled={disabled}
        title={title}
      >
        {buttonLabel}
      </button>
    )
  }

  const { categories, overall_score, resolved_company, source, coverage } = intelligence
  const overallScore = overall_score || 50
  const partialFailures = coverage?.partial_failure_categories || []
  const emptyResults = coverage?.empty_result_categories || []
  const hasCoverageNote = partialFailures.length > 0 || emptyResults.length > 0

  return (
    <div className="company-intelligence">
      <div className="intelligence-header">
        <div className="header-content">
          <h3>🏢 Company Intelligence</h3>
          {resolved_company && (
            <p className="resolved-company">
              Domain: {resolved_company.domain} ({(resolved_company.confidence * 100).toFixed(0)}% confidence)
            </p>
          )}
          {source && (
            <span className="source-badge">{source === 'cache' ? '📦 Cached' : '🔄 Live'}</span>
          )}
        </div>
        {!hideOverallScore && overall_score !== undefined && (
          <div className={`overall-score overall-score-${getOverallScoreColor(overallScore)}`}>
            <div className="score-value">{overallScore}</div>
            <div className="score-label">Overall Score</div>
          </div>
        )}
      </div>

      {hasCoverageNote && (
        <div className="intelligence-coverage">
          <p><strong>Coverage note:</strong> Some categories had limited data.</p>
          {partialFailures.length > 0 && (
            <p>⚠️ Failed categories: {partialFailures.join(', ')}</p>
          )}
          {emptyResults.length > 0 && (
            <p>ℹ️ No results found for: {emptyResults.join(', ')}</p>
          )}
        </div>
      )}

      <div className="intelligence-categories">
        {/* Layoffs */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.layoffs}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.layoffs.score)}`}>
                {categories.layoffs.score}
              </div>
              {renderDeepDiveButton('layoffs')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.layoffs.score)}</p>
          <p className="category-summary">{categories.layoffs.summary}</p>
          {categories.layoffs.explanation && (
            <p className="category-explanation">{categories.layoffs.explanation}</p>
          )}
          {categories.layoffs.warning && (
            <p className="category-warning">⚠️ {categories.layoffs.warning}</p>
          )}
          {categories.layoffs.sources && categories.layoffs.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.layoffs.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Hirings */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.hirings}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.hirings.score)}`}>
                {categories.hirings.score}
              </div>
              {renderDeepDiveButton('hirings')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.hirings.score)}</p>
          <p className="category-summary">{categories.hirings.summary}</p>
          {categories.hirings.explanation && (
            <p className="category-explanation">{categories.hirings.explanation}</p>
          )}
          {categories.hirings.warning && (
            <p className="category-warning">⚠️ {categories.hirings.warning}</p>
          )}
          {categories.hirings.sources && categories.hirings.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.hirings.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Scandals */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.scandals}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.scandals.score)}`}>
                {categories.scandals.score}
              </div>
              {renderDeepDiveButton('scandals')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.scandals.score)}</p>
          <p className="category-summary">{categories.scandals.summary}</p>
          {categories.scandals.explanation && (
            <p className="category-explanation">{categories.scandals.explanation}</p>
          )}
          {categories.scandals.warning && (
            <p className="category-warning">⚠️ {categories.scandals.warning}</p>
          )}
          {categories.scandals.sources && categories.scandals.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.scandals.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Management */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.management}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.management.score)}`}>
                {categories.management.score}
              </div>
              {renderDeepDiveButton('management')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.management.score)}</p>
          <p className="category-summary">{categories.management.summary}</p>
          {categories.management.explanation && (
            <p className="category-explanation">{categories.management.explanation}</p>
          )}
          {categories.management.warning && (
            <p className="category-warning">⚠️ {categories.management.warning}</p>
          )}
          {categories.management.sources && categories.management.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.management.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Employee Reviews */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.reviews_employ}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.reviews_employ.score)}`}>
                {categories.reviews_employ.score}
              </div>
              {renderDeepDiveButton('reviews_employ')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.reviews_employ.score)}</p>
          <p className="category-summary">{categories.reviews_employ.summary}</p>
          {categories.reviews_employ.explanation && (
            <p className="category-explanation">{categories.reviews_employ.explanation}</p>
          )}
          {categories.reviews_employ.warning && (
            <p className="category-warning">⚠️ {categories.reviews_employ.warning}</p>
          )}
          {categories.reviews_employ.sources && categories.reviews_employ.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.reviews_employ.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Customer Reviews */}
        <div className="category-card">
          <div className="category-header">
            <h4>{COMPANY_INTEL_CATEGORY_LABELS.reviews_customer}</h4>
            <div className="category-header-actions">
              <div className={`category-score category-score-${getScoreColor(categories.reviews_customer.score)}`}>
                {categories.reviews_customer.score}
              </div>
              {renderDeepDiveButton('reviews_customer')}
            </div>
          </div>
          <p className="category-label">{getScoreLabel(categories.reviews_customer.score)}</p>
          <p className="category-summary">{categories.reviews_customer.summary}</p>
          {categories.reviews_customer.explanation && (
            <p className="category-explanation">{categories.reviews_customer.explanation}</p>
          )}
          {categories.reviews_customer.warning && (
            <p className="category-warning">⚠️ {categories.reviews_customer.warning}</p>
          )}
          {categories.reviews_customer.sources && categories.reviews_customer.sources.length > 0 && (
            <div className="category-sources">
              <strong>Sources:</strong>
              <ul>
                {categories.reviews_customer.sources.map((source, idx) => (
                  <li key={idx}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Company Size & Growth */}
        {categories.company_size_growth && (
          <div className="category-card">
            <div className="category-header">
              <h4>{COMPANY_INTEL_CATEGORY_LABELS.company_size_growth}</h4>
              <div className="category-header-actions">
                <div className={`category-score category-score-${getScoreColor(categories.company_size_growth.score)}`}>
                  {categories.company_size_growth.score}
                </div>
                {renderDeepDiveButton('company_size_growth')}
              </div>
            </div>
            <p className="category-label">{getScoreLabel(categories.company_size_growth.score)}</p>
            <p className="category-summary">{categories.company_size_growth.summary}</p>
            {categories.company_size_growth.explanation && (
              <p className="category-explanation">{categories.company_size_growth.explanation}</p>
            )}
            {categories.company_size_growth.warning && (
              <p className="category-warning">⚠️ {categories.company_size_growth.warning}</p>
            )}
            {categories.company_size_growth.sources && categories.company_size_growth.sources.length > 0 && (
              <div className="category-sources">
                <strong>Sources:</strong>
                <ul>
                  {categories.company_size_growth.sources.map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Diversity & ESG */}
        {categories.diversity_esg && (
          <div className="category-card">
            <div className="category-header">
              <h4>{COMPANY_INTEL_CATEGORY_LABELS.diversity_esg}</h4>
              <div className="category-header-actions">
                <div className={`category-score category-score-${getScoreColor(categories.diversity_esg.score)}`}>
                  {categories.diversity_esg.score}
                </div>
                {renderDeepDiveButton('diversity_esg')}
              </div>
            </div>
            <p className="category-label">{getScoreLabel(categories.diversity_esg.score)}</p>
            <p className="category-summary">{categories.diversity_esg.summary}</p>
            {categories.diversity_esg.explanation && (
              <p className="category-explanation">{categories.diversity_esg.explanation}</p>
            )}
            {categories.diversity_esg.warning && (
              <p className="category-warning">⚠️ {categories.diversity_esg.warning}</p>
            )}
            {categories.diversity_esg.sources && categories.diversity_esg.sources.length > 0 && (
              <div className="category-sources">
                <strong>Sources:</strong>
                <ul>
                  {categories.diversity_esg.sources.map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Culture & Benefits */}
        {categories.culture_benefits && (
          <div className="category-card">
            <div className="category-header">
              <h4>{COMPANY_INTEL_CATEGORY_LABELS.culture_benefits}</h4>
              <div className="category-header-actions">
                <div className={`category-score category-score-${getScoreColor(categories.culture_benefits.score)}`}>
                  {categories.culture_benefits.score}
                </div>
                {renderDeepDiveButton('culture_benefits')}
              </div>
            </div>
            <p className="category-label">{getScoreLabel(categories.culture_benefits.score)}</p>
            <p className="category-summary">{categories.culture_benefits.summary}</p>
            {categories.culture_benefits.explanation && (
              <p className="category-explanation">{categories.culture_benefits.explanation}</p>
            )}
            {categories.culture_benefits.warning && (
              <p className="category-warning">⚠️ {categories.culture_benefits.warning}</p>
            )}
            {categories.culture_benefits.sources && categories.culture_benefits.sources.length > 0 && (
              <div className="category-sources">
                <strong>Sources:</strong>
                <ul>
                  {categories.culture_benefits.sources.map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Competitors & Market */}
        {categories.competitors_market && (
          <div className="category-card">
            <div className="category-header">
              <h4>{COMPANY_INTEL_CATEGORY_LABELS.competitors_market}</h4>
              <div className="category-header-actions">
                <div className={`category-score category-score-${getScoreColor(categories.competitors_market.score)}`}>
                  {categories.competitors_market.score}
                </div>
                {renderDeepDiveButton('competitors_market')}
              </div>
            </div>
            <p className="category-label">{getScoreLabel(categories.competitors_market.score)}</p>
            <p className="category-summary">{categories.competitors_market.summary}</p>
            {categories.competitors_market.explanation && (
              <p className="category-explanation">{categories.competitors_market.explanation}</p>
            )}
            {categories.competitors_market.warning && (
              <p className="category-warning">⚠️ {categories.competitors_market.warning}</p>
            )}
            {categories.competitors_market.sources && categories.competitors_market.sources.length > 0 && (
              <div className="category-sources">
                <strong>Sources:</strong>
                <ul>
                  {categories.competitors_market.sources.map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
